import {API, Auth, CognitoHostedUIIdentityProvider, graphqlOperation, Storage} from '@/plugins/finAmplifyModules'
import {getUser} from '@/js/finQueries'
import {
  updateUserArchetype,
  updateUsername,
  updateUserOnboarding,
  updateUserPersonData,
  updateUserProfileImage
} from '@/js/finMutations'

import Vue from 'vue'
import ParserTool from "@/components/common/lexicon/LexiconAPI"
import {listPriceModels} from "@/components/pages/profile/graphql"
import store from "@/store/index"

const state = {
  authorized: false,
  user: null,
  profile: null,
  init: false,
  groups: [],
  allPriceModels: [],
}

function getOnboardingData(state) {
  const data = state?.profile?.onboarding
  return data ? JSON.parse(data) : {}
}

const getters = {
  profile: state => state.profile,
  user: state => state.user,
  authorized: state => {
    if (state.authorized) {
      state.authorized.email_verified =
        state.user?.username.startsWith("SignInWithApple_") || state.authorized.email_verified
    }
    return state.authorized
  },
  publicQueryAuthMode: state => state.authorized ? 'AMAZON_COGNITO_USER_POOLS' : 'AWS_IAM',
  title: state => state.authorized ? state.profile.title : '',
  salutation: state => state.authorized ? state.profile.salutation : '',
  firstname: state => state.authorized ? state.profile.firstname : '',
  lastname: state => state.authorized ? state.profile.lastname : '',
  dateOfBirth: state => state.authorized ? state.profile.dateOfBirth : '',
  address: state => state.authorized ? state.profile.address : '',
  username: state => state.authorized ? state.profile.username || state.user.attributes.email : '',
  userId: state => state?.user?.username,
  hasDocumentsToAccept: state => state.profile?.documentsToAccept?.length,
  userOnboardingData: state => {
    return getOnboardingData(state)
  },
  showOnBoarding: state => {
    const data = getOnboardingData(state)

    // FINDEV-2517 if user manages to skip the onboarding -> force onboarding
    if (!data) {
      return true
    }

    // check if user has finished the initial onboarding process
    return !("initialOnboardingFinished" in data)
  },
  onboardingArchetypeID: (state) => {
    const data = getOnboardingData(state)
    return data?.step1?.archeType
  },
  profileArchetype: state => state.authorized ? state.profile.archetype : null,
  groupPriceModel: state => state.authorized && state.profile.group && state.profile.group.priceModel ? state.profile.group.priceModel : null,
  userEmail: state => state.profile.email,
  userNewsletterSubscribed: state => state.profile.newsletter,
  allPriceModels: state => state.allPriceModels
}

const mutations = {
  user(state, user) {
    //console.log("Vuex auth.module changing old user/new user", state.user, user)
    state.authorized = !!user && user.attributes
    state.user = user
    state.init = true
  },
  profile(state, profile) {
    state.profile = profile
  },
  docsAccepted(state) {
    Vue.set(state.profile, 'documentsToAccept', [])
  },
  subscriptionChanged(state, aboId) {
    Vue.set(state.profile, 'subscriptionChange', aboId)
  },
  profileImage(state, image) {
    Vue.set(state.profile, 'image', image)
  },
  title(state, title) {
    Vue.set(state.profile, 'title', title)
  },
  salutation(state, salutation) {
    Vue.set(state.profile, 'salutation', salutation)
  },
  firstname(state, firstname) {
    Vue.set(state.profile, 'firstname', firstname)
  },
  lastname(state, lastname) {
    Vue.set(state.profile, 'lastname', lastname)
  },
  dateOfBirth(state, dateOfBirth) {
    Vue.set(state.profile, 'dateOfBirth', dateOfBirth)
  },
  address(state, address) {
    Vue.set(state.profile, 'address', address)
  },
  username(state, username) {
    Vue.set(state.profile, 'username', username)
  },
  onboarding(state, onboarding) {
    Vue.set(state.profile, 'onboarding', onboarding)
  },
  version(state, version) {
    Vue.set(state.profile, '_version', version)
  },
  archetype(state, archetype) {
    Vue.set(state.profile, 'archetype', archetype)
  },
  archetypeUpdatedAt(state, archetypeUpdatedAt) {
    Vue.set(state.profile, 'archetypeUpdatedAt', archetypeUpdatedAt)
  },
  groups(state, groups) {
    state.groups = groups
  },
  allPriceModels(state, priceModels) {
    state.allPriceModels = priceModels
  }
}

const actions = {
  async init({commit, dispatch}) {
    if (!state.init) {
      try {
        await Promise.all([
          ParserTool.loadParserIndex(),
          ParserTool.loadContextHelpIndex()
        ])
      } catch (e) {
        console.error('failed to load parserIndex / ContextHelpIndex', e)
      }

      console.log("trying to fetch user per fetchUser")
      await dispatch('fetchUser').catch(async e => {
        console.log("failed to fetch user per fetchUser", e)
      })
    }
    return state
  },
  async signOut({commit, state}) {
    try {
      // global: signs out user on ALL devices
      await state.user.signOut({global: true})
      commit('profile', null)
      commit('user', null)
    } catch (error) {
      console.error("Error signing out", error)
    }
  },
  signIn({dispatch, state}, {email, password}) {
    return new Promise((resolve, reject) => {
      Auth.signIn(email, password).then(
        user => {
          dispatch('fetchUser').then(user => {
            resolve(user)
          }).catch(reject)
        }
      ).catch(reject)
    })
  },
  federatedSignInApple({dispatch, state}) {
    return Auth.federatedSignIn({provider: CognitoHostedUIIdentityProvider.Apple})
  },
  federatedSignInGoogle({dispatch, state}) {
    return Auth.federatedSignIn({provider: CognitoHostedUIIdentityProvider.Google})
  },
  refreshSession({dispatch, commit, state}) {
    //console.log(`refreshSession - 'old' vuex user`, state.user)
    // see https://docs.amplify.aws/lib/auth/manageusers/q/platform/js/#retrieve-attributes-for-current-authenticated-user
    return Auth.currentSession()
      .then(data => {
        //console.log("Session refreshed", data)
        dispatch('fetchUser')
      })
      .catch(err => {
        console.error("Error refreshing user session", err)
        throw err
      })
    /*return new Promise((resolve, reject) => {
      state.user.refreshSession(state.user.signInUserSession.refreshToken, async (err, session) => {
        if (err) {
          console.error("Error calling user.refreshSession", err)
          reject(err)
        } else {
          console.log("Successfully calling user.refreshSession")
          await dispatch('fetchUser')
          resolve(session)
        }
      })
    })*/
  },
  async fetchUser({commit, dispatch}) {
    try {
      const user = await Auth.currentAuthenticatedUser({
        bypassCache: true  // Optional, By default is false. If set to true, this call will send a request to Cognito
                           // to get the latest user data
      })

      console.log("fetchUser user", user)
      const id = user.username
      const profile = await API.graphql(graphqlOperation(getUser, {id}))
      const allPriceModels = await API.graphql({
        query: listPriceModels,
        authMode: store.getters["auth/publicQueryAuthMode"]
      })

      /* if(!profile.identityId)  {
         const {identityId} = await Auth.currentUserCredentials()
         const updateUserIdentityId = `
         mutation UpdateUser(
           $input: UpdateUserInput!
           $condition: ModelUserConditionInput
         ) {
           updateUser(input: $input, condition: $condition) {
             identityId
           }
         }
         `
         await API.graphql(graphqlOperation(updateUserIdentityId, {id, identityId}))
         profile.identityId=identityId
       }*/
      commit('profile', profile.data.getUser || {id, dummy: true})
      commit('user', user)
      commit('groups', user.signInUserSession.idToken.payload["cognito:groups"])
      commit('allPriceModels', allPriceModels.data.listPriceModels.items.sort((a, b) => a.order - b.order))
      return user
    } catch (e) {
      console.log("failed to get user")
      commit('user', null)
      commit('profile', null)
      commit('groups', [])
      throw e
    }
  },
  updateUserPersonData({commit, state}, {title, salutation, firstname, lastname, dateOfBirth, address}) {
    title = !title ? '' : title
    salutation = !salutation ? '' : salutation
    firstname = !firstname ? '' : firstname
    lastname = !lastname ? '' : lastname
    address = !address ? {street: "", streetNo: "", zipCode: "", city: "", country: ""} : address

    const id = state.user.username

    let inputObj = {
      id: id,
      title: title,
      salutation: salutation,
      firstname: firstname,
      lastname: lastname,
    }

    if (dateOfBirth) {
      inputObj.dateOfBirth = dateOfBirth
    }

    if (address) {
      inputObj.address = address
    }

    return new Promise((resolve, reject) => {
      API.graphql(graphqlOperation(updateUserPersonData, {
        input: inputObj
      })).then(profile => {
        state.profile.dummy = false
        commit('title', title)
        commit('salutation', salutation)
        commit('firstname', firstname)
        commit('lastname', lastname)
        commit('dateOfBirth', dateOfBirth)
        commit('address', address)
        resolve(title, salutation, firstname, lastname, dateOfBirth, address)
      }).catch(reject)
    })
  },
  updateUsername({commit, state}, username) {
    if (!username) {
      username = ''
    }
    return new Promise((resolve, reject) => {
      console.log("state.user in updateUsername", state.user)
      const id = state.user.username
      // const version = state.profile._version
      API.graphql(graphqlOperation(updateUsername, {
        input: {
          id: id,
          username: username
          // _version : version
        }
      })).then(profile => {
        state.profile.dummy = false
        // commit('version', profile.data.updateUser._version )
        commit('username', username)
        resolve(username)
      }).catch(reject)
    })
  },
  updateOnboarding({commit, state}, onboarding) {
    return new Promise((resolve, reject) => {
      const id = state.user.username
      API.graphql(graphqlOperation(updateUserOnboarding, {
        input: {
          id,
          onboarding
        }
      })).then(result => {
        state.profile.dummy = false
        commit('onboarding', onboarding)
        resolve(result)
      }).catch(reject)
    })
  },
  updateProfileImage({commit, state}, blob) {
    const format = 'image/png'
    const fileName = 'images/avatar_.png'

    return new Promise((resolve, reject) => {
      commit('profileImage', null)
      Storage.put(fileName, new File([blob], fileName), {
        level: 'protected',
        contentType: format
      }).then(result => {
        const id = state.user.username
        API.graphql(graphqlOperation(updateUserProfileImage, {
          input: {
            id: id,
            image: result.key
          }
        })).then(profile => {
          state.profile.dummy = false
          commit('profileImage', result.key)
          resolve(result.key)
        }).catch(reject)
      }).catch(reject)
    })
  },
  updateArchetype({commit, state, getters}, archetypeId) {
    return new Promise((resolve, reject) => {
      const id = state.user.username
      API.graphql(graphqlOperation(updateUserArchetype, {
        input: {
          id: id,
          userArchetypeId: archetypeId,
          archetypeUpdatedAt: new Date()
        }
      })).then(profile => {
        let archetype = profile.data.updateUser.archetype
        let archetypeUpdatedAt = profile.data.updateUser.archetypeUpdatedAt
        commit('archetype', archetype)
        commit('archetypeUpdatedAt', archetypeUpdatedAt)
        resolve(archetype)
      }).catch(reject)
    })
  }
}

export const auth = {
  namespaced: true,
  state,
  getters,
  mutations,
  actions
}
